import { FeedbackType } from '@prisma/client-cadence'
import { Bug, Lightbulb, HelpCircle, Heart } from '@/components/icons'

/**
 * The four feedback types the modal offers, with the token classes each one
 * paints when selected.
 *
 * Extracted from `feedback-modal.tsx` so that file stays under the 1000-line
 * source cap (`check:file-size-ratchet`). It is a static table with no state
 * and no hooks, so it lives cleanly beside the modal rather than inside it.
 */
export const feedbackTypes = [
  {
    value: 'BUG' as FeedbackType,
    label: 'Bug Report',
    icon: Bug,
    description: 'Something is broken or not working',
    selectedBg: 'border-status-red-border-medium bg-status-red-bg-light ',
    selectedText: 'text-status-red-text',
    selectedLabel: 'text-status-red-text-label',
  },
  {
    value: 'SUGGESTION' as FeedbackType,
    label: 'Suggestion',
    icon: Lightbulb,
    description: 'An idea for improvement',
    selectedBg: 'border-status-yellow-border-medium bg-status-yellow-bg-light ',
    selectedText: 'text-status-yellow-text',
    selectedLabel: 'text-status-yellow-text-label',
  },
  {
    value: 'QUESTION' as FeedbackType,
    label: 'Question',
    icon: HelpCircle,
    description: 'Need help or clarification',
    selectedBg: 'border-status-purple-border-medium bg-status-purple-bg-light ',
    selectedText: 'text-status-purple-text',
    selectedLabel: 'text-status-purple-text-label',
  },
  {
    value: 'PRAISE' as FeedbackType,
    label: 'Praise',
    icon: Heart,
    description: 'Something you love about the app',
    selectedBg: 'border-status-pink-border-medium bg-status-pink-bg-light ',
    selectedText: 'text-status-pink-text',
    selectedLabel: 'text-status-pink-text-label',
  },
]

/** Sentinel ID for the captured screenshot attachment. */
export const SCREENSHOT_ATTACHMENT_ID = '__screenshot__'

/**
 * Shown when getDisplayMedia capture fails (not cancelled). There is no
 * server-side fallback any more (#14858), so the user is pointed at the
 * attachment paths that remain.
 */
export const SCREEN_CAPTURE_FAILED_MESSAGE =
  'Screen capture failed. Attach an image with Add file or paste it instead.'

/** Display metadata for the captured screenshot, held for one modal mount. */
export type ScreenshotDisplay = {
  fileName: string
  mimeType: string
  sizeBytes: number
  preview?: string
}

/**
 * What a resumed draft's screenshot ref renders as before (or without) a
 * capture in this mount: the URL is the persisted part, the thumbnail and byte
 * size are not, and re-fetching them to fill a 48px square is not worth a
 * request.
 */
export const RESTORED_SCREENSHOT_DISPLAY: ScreenshotDisplay = {
  fileName: 'screenshot.png',
  mimeType: 'image/png',
  sizeBytes: 0,
}
